import { useMemo, useState, useEffect } from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { useStepScreenshot } from "@/hooks/use-step-screenshot";
import { cn } from "@/lib/utils";
import {
  type HighlightTone,
  type TargetBox,
  isValidBox,
  resolveBoxViewport,
  resolveHighlightStyles,
} from "../screenshot-types";
import { StepThumbnail } from "../step-thumbnail";

interface TargetCropThumbnailProps {
  walkthroughId: string | null | undefined;
  stepNumber: number | null | undefined;
  targetBox?: TargetBox | null;
  targetTone?: HighlightTone | string | null;
  alt: string;
  className?: string;
  zoomLevel?: "crop" | "full";
}

/**
 * High-performance pure-SVG cropped thumbnail focused specifically on the target element.
 * Magnifies the annotated region with zero image-processing backend overhead.
 */
export function TargetCropThumbnail({
  walkthroughId,
  stepNumber,
  targetBox,
  targetTone,
  alt,
  className,
  zoomLevel = "crop",
}: TargetCropThumbnailProps) {
  const { url, loading } = useStepScreenshot(walkthroughId, stepNumber);
  const [naturalSize, setNaturalSize] = useState<{
    width: number;
    height: number;
  } | null>(null);

  useEffect(() => {
    if (!url) return;
    const img = new Image();
    img.src = url;
    img.onload = () => {
      if (img.naturalWidth > 0 && img.naturalHeight > 0) {
        setNaturalSize({
          width: img.naturalWidth,
          height: img.naturalHeight,
        });
      }
    };
  }, [url]);

  const hasValidTarget = isValidBox(targetBox);

  // If no targetBox or zoomed out mode, fall back to standard step thumbnail
  if (!hasValidTarget || zoomLevel === "full") {
    return (
      <StepThumbnail
        walkthroughId={walkthroughId}
        stepNumber={stepNumber}
        targetBox={targetBox}
        targetTone={targetTone}
        alt={alt}
        className={className}
      />
    );
  }

  if (loading) {
    return (
      <Skeleton className={cn("aspect-video w-full rounded", className)} />
    );
  }

  if (!url) {
    return (
      <div
        className={cn(
          "flex aspect-video w-full items-center justify-center rounded bg-muted/30 text-2xs text-muted-foreground",
          className,
        )}
      >
        No preview
      </div>
    );
  }

  const { vw, vh } = resolveBoxViewport(targetBox, naturalSize);

  // Calculate crop window with comfortable padding around the problem element
  const padX = Math.max(48, targetBox.width * 0.4);
  const padY = Math.max(36, targetBox.height * 0.4);

  const cropX = Math.max(0, targetBox.x - padX);
  const cropY = Math.max(0, targetBox.y - padY);
  const cropW = Math.min(vw - cropX, Math.max(120, targetBox.width + padX * 2));
  const cropH = Math.min(vh - cropY, Math.max(80, targetBox.height + padY * 2));

  const styles = resolveHighlightStyles(targetTone);

  return (
    <div
      className={cn(
        "relative flex h-full w-full items-center justify-center overflow-hidden rounded border border-border/60 bg-muted/10",
        className,
      )}
    >
      <svg
        viewBox={`${cropX} ${cropY} ${cropW} ${cropH}`}
        preserveAspectRatio="xMidYMid slice"
        className="h-full w-full select-none"
        role="img"
        aria-label={alt}
      >
        {/* Full raster background positioned in native coordinates */}
        <image href={url} width={vw} height={vh} preserveAspectRatio="none" />

        {/* Outer high-contrast black boundary */}
        <rect
          x={targetBox.x}
          y={targetBox.y}
          width={targetBox.width}
          height={targetBox.height}
          rx={4}
          fill="none"
          stroke="rgba(0,0,0,0.7)"
          strokeWidth={3}
          style={{ vectorEffect: "non-scaling-stroke" }}
        />

        {/* Themed colored highlight stroke & tint fill */}
        <rect
          x={targetBox.x}
          y={targetBox.y}
          width={targetBox.width}
          height={targetBox.height}
          rx={4}
          fill={styles.fill}
          stroke={styles.stroke}
          strokeWidth={2}
          style={{ vectorEffect: "non-scaling-stroke" }}
        />
      </svg>
    </div>
  );
}
